import { InjectedIntl } from "react-intl";
import {
  ChangesSide,
  CompareChangeType,
  CompareContentItem,
  CurrentlyLiveSide,
} from "src/components/CompareChangesModal/types";
import { RefType } from "src/constants/constants";
import { appInstanceIconUrlForPlaylist } from "src/helpers/appHelper";
import { generateImgixThumbnail } from "src/helpers/mediaHelper";
import {
  DraftUpdatedByUser,
  toLastEditedBy,
} from "src/components/CompareChangesModal/utils/toLastEditedBy";
import {
  DiffEntry,
  ItemLike,
  comparePlaylist,
} from "src/utils/publishedDraftDiff";
import { PlaylistCompareChangesSideFragment } from "src/types.g";
import { formatDurationLabel } from "src/components/CompareChangesModal/utils/formatDurationLabel";

/**
 * Maps a playlist's published-vs-draft state into the Compare Changes modal
 * props (GRWBKK-1656). Pure: all inputs come from the
 * playlistCompareChangesById query; no fetching or React state.
 */

type CompareSide = PlaylistCompareChangesSideFragment;

export type CompareDraftSide = CompareSide & {
  userByUpdatedBy?: DraftUpdatedByUser | null;
};

interface ContentListItem extends ItemLike {
  content: {
    _ref?: { id: string; type: string };
    props?: { duration?: number };
  };
}

interface ContentProps {
  play_mode?: string;
  total_items?: number;
  total_durations?: number;
  default_durations?: Record<string, number>;
}

interface ResolvedEntity {
  title: string;
  subtitle: string;
  thumbnailUrl?: string;
  isAppIcon?: boolean;
  /** Video/audio intrinsic duration — wins over custom/default (files only). */
  intrinsicDurationMs?: number;
  /** appInstanceDurationInSeconds in ms — wins over the app default. */
  fixedAppDurationMs?: number;
  /** PDFs fall back to the document default instead of the image default. */
  isDocument?: boolean;
  mimetype?: string;
}

export interface PreparePlaylistCompareChangesOptions {
  intl: InjectedIntl;
  secureMediaPolicy?: string;
}

export interface PlaylistCompareChanges {
  currentlyLive: CurrentlyLiveSide;
  changes: ChangesSide;
}

const SUBTITLE_MESSAGES: Record<
  string,
  { id: string; defaultMessage: string }
> = {
  [RefType.APP]: {
    id: "compare_changes.subtitle.app",
    defaultMessage: "App",
  },
  [RefType.FILE]: {
    id: "compare_changes.subtitle.media",
    defaultMessage: "Media",
  },
  [RefType.LINK]: {
    id: "compare_changes.subtitle.link",
    defaultMessage: "Link",
  },
  [RefType.SITE]: {
    id: "compare_changes.subtitle.site",
    defaultMessage: "Site",
  },
};

const buildEntityIndex = (
  sides: CompareSide[],
  options: PreparePlaylistCompareChangesOptions,
): Map<string, ResolvedEntity> => {
  const { intl, secureMediaPolicy } = options;
  const index = new Map<string, ResolvedEntity>();
  const subtitle = (refType: string) =>
    SUBTITLE_MESSAGES[refType]
      ? intl.formatMessage(SUBTITLE_MESSAGES[refType])
      : "";

  // Later sides overwrite earlier ones, so pass published first and draft
  // second: edited entities display their draft values.
  for (const side of sides) {
    for (const app of side.appInstancesByPlaylistId?.nodes ?? []) {
      const fixedSeconds = app.config?.appInstanceDurationInSeconds;
      index.set(app.id, {
        title: app.name ?? "",
        subtitle: subtitle(RefType.APP),
        thumbnailUrl: appInstanceIconUrlForPlaylist(app) || undefined,
        isAppIcon: true,
        fixedAppDurationMs: fixedSeconds ? fixedSeconds * 1000 : undefined,
      });
    }
    for (const file of side.filesByPlaylistId?.nodes ?? []) {
      const mediaFileOutput = file.fileOutputsByFileId.nodes.find(
        (output) =>
          output?.mimetype?.startsWith("video") ||
          output?.mimetype?.startsWith("audio"),
      );
      index.set(file.id, {
        title: file.name,
        subtitle: subtitle(RefType.FILE),
        thumbnailUrl:
          generateImgixThumbnail(file, secureMediaPolicy, true) || undefined,
        intrinsicDurationMs: mediaFileOutput?.metadata?.duration ?? undefined,
        isDocument: file.mimetype === "application/pdf",
        mimetype: file.mimetype ?? undefined,
      });
    }
    for (const link of side.linksByPlaylistId?.nodes ?? []) {
      index.set(link.id, {
        title: link.name,
        subtitle: subtitle(RefType.LINK),
        thumbnailUrl: link.fileByFileId
          ? generateImgixThumbnail(
              link.fileByFileId,
              secureMediaPolicy,
              true,
            ) || undefined
          : undefined,
      });
    }
    for (const site of side.sitesByPlaylistId?.nodes ?? []) {
      index.set(site.id, {
        title: site.name,
        subtitle: subtitle(RefType.SITE),
        thumbnailUrl: site.fileByThumbnailId
          ? generateImgixThumbnail(
              site.fileByThumbnailId,
              secureMediaPolicy,
              true,
            ) || undefined
          : undefined,
      });
    }
  }
  return index;
};

const getEffectiveDurationMs = (
  item: ContentListItem,
  entity: ResolvedEntity | undefined,
  defaultDurations: Record<string, number>,
): number => {
  const customDuration = item.content.props?.duration || 0;
  const refType = item.content._ref?.type;

  if (refType === RefType.FILE) {
    if (entity?.intrinsicDurationMs) {
      return entity.intrinsicDurationMs;
    }
    if (customDuration) {
      return customDuration;
    }
    return entity?.isDocument
      ? (defaultDurations.document ?? 0)
      : (defaultDurations.image ?? 0);
  }

  if (customDuration) {
    return customDuration;
  }
  if (refType === RefType.APP && entity?.fixedAppDurationMs) {
    return entity.fixedAppDurationMs;
  }
  return (refType && defaultDurations[refType]) || 0;
};

interface ResolveRowOptions {
  item: ContentListItem;
  position: number;
  entityIndex: Map<string, ResolvedEntity>;
  defaultDurations: Record<string, number>;
  intl: InjectedIntl;
}

const resolveRow = ({
  item,
  position,
  entityIndex,
  defaultDurations,
  intl,
}: ResolveRowOptions): CompareContentItem => {
  const ref = item.content._ref;
  const entity = ref ? entityIndex.get(ref.id) : undefined;
  if (!ref || !entity) {
    return {
      id: item.list_id,
      position,
      title: intl.formatMessage({
        id: "compare_changes.unknown_item",
        defaultMessage: "Unknown item",
      }),
    };
  }
  return {
    id: item.list_id,
    position,
    title: entity.title,
    subtitle: entity.subtitle || undefined,
    thumbnailUrl: entity.thumbnailUrl,
    isAppIcon: entity.isAppIcon,
    durationLabel: formatDurationLabel(
      getEffectiveDurationMs(item, entity, defaultDurations),
    ),
    contentRef: { type: ref.type, id: ref.id, mimetype: entity.mimetype },
  };
};

const CHANGE_TYPE_BY_OP: Record<DiffEntry<ItemLike>["op"], CompareChangeType> =
  {
    equal: "unchanged",
    insert: "added",
    delete: "removed",
    move: "reordered",
    update: "edited",
  };

const hasEffectiveDurationChange = (
  entry: Extract<DiffEntry<ContentListItem>, { op: "update" }>,
  entity: ResolvedEntity | undefined,
  publishedDefaults: Record<string, number>,
  draftDefaults: Record<string, number>,
): boolean =>
  getEffectiveDurationMs(entry.published, entity, publishedDefaults) !==
  getEffectiveDurationMs(entry.draft, entity, draftDefaults);

const toChangesItem = (
  entry: DiffEntry<ContentListItem>,
  position: number,
  entityIndex: Map<string, ResolvedEntity>,
  options: PreparePlaylistCompareChangesOptions & {
    publishedDefaults: Record<string, number>;
    draftDefaults: Record<string, number>;
  },
): CompareContentItem => {
  const { intl, publishedDefaults, draftDefaults } = options;
  const item = entry.op === "update" ? entry.draft : entry.item;
  const refId = item.content._ref?.id;
  const entity = refId ? entityIndex.get(refId) : undefined;
  const base = resolveRow({
    item,
    position,
    entityIndex,
    defaultDurations: draftDefaults,
    intl,
  });

  // A default_durations change edits the effective duration of items that
  // inherit it even though their own props are untouched (GRWBKK-1773). Only
  // both-sides entries with equal props can be derived-edited; unknown-ref
  // fallback rows are skipped as they have no duration to display.
  const isDerivedEdited =
    (entry.op === "equal" || entry.op === "move") &&
    !!entity &&
    getEffectiveDurationMs(item, entity, publishedDefaults) !==
      getEffectiveDurationMs(item, entity, draftDefaults);

  const changeType: CompareChangeType = isDerivedEdited
    ? "edited"
    : CHANGE_TYPE_BY_OP[entry.op];

  // Per design (Figma 3477-26372): the changes column shows a duration only on
  // rows whose effective duration changed. Every other row omits it.
  const showDuration =
    entry.op === "update"
      ? hasEffectiveDurationChange(
          entry,
          entity,
          publishedDefaults,
          draftDefaults,
        )
      : isDerivedEdited;
  const durationLabel = showDuration ? base.durationLabel : undefined;

  const moved =
    (entry.op === "update" && entry.moved) ||
    (entry.op === "move" && isDerivedEdited)
      ? { movedFrom: entry.fromIndex + 1, movedTo: entry.toIndex + 1 }
      : undefined;

  return {
    ...base,
    durationLabel,
    changeType,
    ...moved,
  };
};

export const preparePlaylistCompareChanges = (
  playlist: CompareSide & { draft: CompareDraftSide | null },
  options: PreparePlaylistCompareChangesOptions,
): PlaylistCompareChanges => {
  const { intl } = options;
  const publishedList = (playlist.content?.list ?? []) as ContentListItem[];
  const publishedProps = (playlist.content?.props ?? {}) as ContentProps;
  const draftList = (playlist.draft?.content?.list ?? []) as ContentListItem[];
  const draftProps = (playlist.draft?.content?.props ?? {}) as ContentProps;
  const publishedDefaults = publishedProps.default_durations ?? {};
  const draftDefaults = draftProps.default_durations ?? {};

  // Published first, draft second: draft entities win on conflict.
  const entityIndex = buildEntityIndex(
    [playlist, ...(playlist.draft ? [playlist.draft] : [])],
    options,
  );

  const { entries } = comparePlaylist(publishedList, draftList);

  const currentlyLive: CurrentlyLiveSide = {
    publishedAt: playlist.publishedAt ?? undefined,
    items: publishedList.map((item, i) =>
      resolveRow({
        item,
        position: i + 1,
        entityIndex,
        defaultDurations: publishedDefaults,
        intl,
      }),
    ),
  };

  const items = entries.map((entry, i) =>
    toChangesItem(entry, i + 1, entityIndex, {
      ...options,
      publishedDefaults,
      draftDefaults,
    }),
  );
  // Per feedback on GRWBKK-1773: settings changes are not shown or counted —
  // a default_durations change surfaces through the items it derived-edits.
  const totalChanges = items.filter(
    (item) => item.changeType && item.changeType !== "unchanged",
  ).length;

  const changes: ChangesSide = {
    items,
    totalChanges,
    lastEditedBy: toLastEditedBy(playlist.draft?.userByUpdatedBy),
  };

  return { currentlyLive, changes };
};
